import React, { useState, useEffect, ReactElement } from 'react';
import lodashMerge from 'lodash.merge';
import { useTranslation } from 'react-i18next';
import { Table } from 'common/components/antd/Table/Table';
import { useGetLimitsOperationsDataQuery } from 'domains/limits/queries/limits.query';
import {
  TGetLimitsOperationsDataResponse,
  TGetLimitsOperationsAssetRow,
} from 'domains/limits/types/limits.types';
import { TSegmentsResponse } from 'domains/segments/types/segments.types';
import getPeriodTableColumns from './no-period-table.columns';
import { ENoPeriodTableColumns } from './no-period-table.enum';
import { TOperationsLimitsTableRecord } from './no-period-table.types';
import { ETypes } from '../../enums/limits.enum';
import SetLimitsModal from '../../modals/set-limits.modal';
import { TStandardLimitsModalData } from '../../types/standard-limits.types';

type TProps = {
  dataKey: string[][];
  tableType: ETypes;
  segmentsData: TSegmentsResponse;
};

const PeriodTableComponent = (props: TProps): ReactElement => {
  const {
    dataKey,
    tableType,
    segmentsData,
  } = props;
  const counterpartyData = dataKey[0];
  const counterpartyLabel = counterpartyData[0];
  const counterpartyGUID = counterpartyData[1];
  const segmentData = dataKey[1];
  const segmentLabel = segmentData[0];
  const segmentKey = segmentData[1];
  const { t } = useTranslation();
  const [modalData, setModalData] = useState<TStandardLimitsModalData | {}>({});

  const limitsOperationsDataSelectQuery = (limitsOperationsDataResponse: TGetLimitsOperationsDataResponse): (any)[] => {
    const currentLimitsData = segmentsData.filter(
      currentLimitData => currentLimitData.counterparty.guid === counterpartyGUID
    )[0];
    const assetsList = currentLimitsData.segments[segmentKey];
    const formattedLimitsUsersData = (limitsOperationsDataResponse?.columns || [])
      .reduce((limitsAcc: { [key: string]: TGetLimitsOperationsAssetRow}, limitData: TGetLimitsOperationsAssetRow) => ({
        ...limitsAcc,
        [limitData.account_type_guid]: limitData,
      }), {});
    const dataTableSkeleton = assetsList.map(({ account_type_guid, asset_code, asset_issuer }) => {
      const dataAssetRowFromApi = formattedLimitsUsersData[account_type_guid];
      const dataAssetRowSkeleton = {
        account_type_guid,
        asset_code,
        asset_issuer,
        min: null,
        max: null,
      };

      return lodashMerge(dataAssetRowSkeleton, dataAssetRowFromApi);
    });

    return dataTableSkeleton;
  };

  const {
    data: limitsOperationsData,
    isLoading: isLoadingLimitsOperationsData,
    isError: isErrorLimitsOperationsData,
    error: errorLimitsOperationsData,
    refetch: refetchLimitsOperationsData,
  } = useGetLimitsOperationsDataQuery(
    counterpartyGUID,
    {
      select: limitsOperationsDataSelectQuery,
      enabled: tableType === ETypes.Operation,
    }
  );

  useEffect(() => {
    if (isErrorLimitsOperationsData) {
      console.error(errorLimitsOperationsData);
    }
  }, [isErrorLimitsOperationsData, errorLimitsOperationsData]);

  const onCancelModalHandler = (): void => {
    setModalData({});
  };

  const invalidateDataTableHandler = async (): Promise<void> => {
    try {
      await refetchLimitsOperationsData();
    } catch (error) {
      console.error(error);
    }
  };

  const onCellClickHandler = (record: TOperationsLimitsTableRecord): void => {
    const periodLabel = t('limits.standardLimits.modal.edit.fields.label.noPeriodDays');
    const maxAmount = record.max;
    const minAmount = record.min;

    const collectModalData: TStandardLimitsModalData = {
      accountTypeGuid: record?.account_type_guid,
      counterpartyLabel,
      counterpartyGUID,
      segmentLabel,
      typeLabel: tableType,
      periodLabel,
      maxAmount,
      minAmount,
      assetCode: record.asset_code,
      assetIssuer: record.asset_issuer,
    };
    setModalData(collectModalData);
  };

  const columns = getPeriodTableColumns(onCellClickHandler);

  return (
    <>
      <Table
        columns={columns}
        dataSource={limitsOperationsData as TOperationsLimitsTableRecord[]}
        rowKey={ENoPeriodTableColumns.AccountTypeGuid}
        loading={isLoadingLimitsOperationsData}
      />
      <SetLimitsModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TStandardLimitsModalData}
        tableType={tableType}
        invalidateDataTableHandler={invalidateDataTableHandler}
      />
    </>
  );
};

export default PeriodTableComponent;
